/**
 * Ein Element in seinem eigenen Scroll-Behälter in den Blick holen.
 *
 * Warum nicht `scrollIntoView`: Das verschiebt JEDEN scrollbaren Vorfahren,
 * auch einen mit `overflow: hidden`, und den zieht niemand zurück. Im
 * Karten-Fenster hing dadurch der ganze Fensterinhalt samt Fußzeile nach oben
 * verschoben, unten stand Weißraum, und der Kopf der Karte war nicht mehr
 * erreichbar (Fabian, 17.09.2026, Karte aus einer Erwähnung geöffnet). Der
 * Fenster-Körper trägt sein `overflow-hidden` mit Absicht (ADR-0097), also
 * bewegt dieser Helfer genau einen Knoten: den nächsten Vorfahren, der wirklich
 * scrollt.
 *
 * Findet er keinen, meldet `scrollImBehaelter` `false` und bewegt nichts. Ein
 * Rückfall auf `scrollIntoView` ist nur dort vertretbar, wo kein Vorfahre den
 * Überlauf versteckt (die Vollseite unter der `AppShell`, in der das Dokument
 * scrollt). Dieser bewachte Rückfall steht hier in `scrollInsBild` und nur
 * hier: Er lag bis zum 18.09.2026 wortgleich in zwei Komponenten
 * (`customer-cockpit-header.tsx`, `zeitstrahl-reiter.tsx`), und zwei Kopien
 * derselben Bewachung driften auseinander.
 *
 * Wann welche Funktion: `scrollImBehaelter` für Ziele, die immer in einem
 * Behälter liegen (Karten-Fenster, Dialog, Dock). `scrollInsBild` für Ziele,
 * die je nach Weg im Behälter oder auf der Vollseite stehen können.
 */

import { prefersReducedMotion } from '~/lib/scroll-to-anchor';

/**
 * Wohin das Ziel im Behälter soll.
 *
 * `mitte`: mittig, auch wenn es schon zu sehen ist. Der Sprung aus einer
 * Erwähnung soll den gemeinten Beitrag eindeutig zeigen.
 * `naechste`: nur so weit, dass das Ziel vollständig im Behälter liegt. Steht
 * es schon im Bild, bleibt der Behälter stehen.
 * `anfang`: die Oberkante des Ziels an die Oberkante des Behälters, immer.
 * Für ein Ziel, das länger ist als der Ausschnitt (eine ganze Spalte): Bei
 * `naechste` käme seine Unterkante ins Bild und der Anfang läge weit darüber.
 * Ein `scroll-margin-top` am Ziel (`scroll-mt-*`) hält dabei Abstand, etwa zu
 * einer klebenden Leiste darüber (N-5, 20.09.2026).
 */
export type ScrollLage = 'mitte' | 'naechste' | 'anfang';

/**
 * Tailwind-Klassen, die einen Scroll-Behälter machen.
 *
 * jsdom rechnet keine Stylesheets aus: `getComputedStyle` kennt dort die
 * Tailwind-Regeln nicht und meldet für jeden Knoten `visible`. Deshalb zwei
 * Quellen, die Klasse und der gerechnete Stil.
 */
const SCROLL_KLASSEN = ['overflow-y-auto', 'overflow-auto', 'overflow-y-scroll'];

/**
 * Tailwind-Klassen, die nur waagerecht scrollen lassen.
 *
 * Sie stehen an Chip-Leisten und breiten Tabellen. Im Browser meldet
 * `getComputedStyle` für so einen Knoten trotzdem `overflowY: auto`: Nach dem
 * CSS Overflow Module Level 3 (berechneter Wert von `overflow`, „if one
 * cascaded value is visible and the other is not, visible computes to auto")
 * zieht eine `auto`- oder `scroll`-Achse die andere mit (`clip` nicht, darauf
 * baut `styles.css` mit `overflow-x: clip` an `html` und `body`). Ohne diese Liste griffe der
 * Helfer eine waagerechte Leiste als senkrechten Behälter und bewegte nichts
 * Sichtbares (Prüfbefund 18.09.2026). Trägt der Knoten zusätzlich eine Klasse
 * aus `SCROLL_KLASSEN`, scrollt er wirklich senkrecht und gilt. Die Regel
 * greift nur bei Tailwind-Klassen: eine waagerechte Leiste mit eigenem CSS
 * gilt weiterhin als senkrechter Behälter.
 */
const WAAGERECHT_KLASSEN = ['overflow-x-auto', 'overflow-x-scroll'];

/**
 * Tailwind-Klassen, die den senkrechten Überlauf abschneiden. Wer eine davon
 * trägt, bliebe nach `scrollIntoView` verschoben stehen.
 */
const UEBERLAUF_VERSTECKT = [
  'overflow-hidden',
  'overflow-clip',
  'overflow-y-hidden',
  'overflow-y-clip',
] as const;

/**
 * Scrollt `ziel` innerhalb seines nächsten scrollenden Vorfahren in den Blick.
 *
 * Ohne eigene Angabe folgt `verhalten` der Systemeinstellung „Bewegung
 * reduzieren": wer sie gesetzt hat, bekommt den harten Sprung statt der
 * Animation.
 *
 * @returns `true`, wenn ein Scroll-Behälter gefunden wurde (auch dann, wenn das
 *   Ziel bei `naechste` schon im Bild stand und nichts zu tun war), sonst
 *   `false`.
 */
export function scrollImBehaelter(
  ziel: HTMLElement,
  lage: ScrollLage,
  verhalten: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth',
): boolean {
  const behaelter = naechsterScrollBehaelter(ziel);
  if (behaelter === null) {
    return false;
  }

  // Abstand der Oberkante des Ziels zur Oberkante des Behälters, im aktuellen
  // Scroll-Stand gemessen. Plus `scrollTop` ergibt das die Lage des Ziels im
  // Inhalt des Behälters.
  const abstand = ziel.getBoundingClientRect().top - behaelter.getBoundingClientRect().top;
  const oben = behaelter.scrollTop + abstand;
  const zielHoehe = ziel.offsetHeight;
  const sichtHoehe = behaelter.clientHeight;

  if (lage === 'mitte') {
    scrolleAuf(behaelter, oben - (sichtHoehe - zielHoehe) / 2, verhalten);
    return true;
  }
  if (lage === 'anfang') {
    scrolleAuf(behaelter, oben - scrollRandOben(ziel), verhalten);
    return true;
  }
  if (abstand < 0) {
    // Ragt oben heraus: Oberkante bündig.
    scrolleAuf(behaelter, oben, verhalten);
    return true;
  }
  if (abstand + zielHoehe > sichtHoehe) {
    // Ragt unten heraus: Unterkante bündig.
    scrolleAuf(behaelter, oben + zielHoehe - sichtHoehe, verhalten);
    return true;
  }
  // Schon im Bild: nichts tun, sonst zuckt die Fläche bei jedem Neuaufbau.
  return true;
}

/**
 * Holt `ziel` ins Bild: erst im Behälter, sonst bewacht im Dokument.
 *
 * Die eine Stelle für einen Sprung im dc Wingman. Gibt es einen scrollenden
 * Vorfahren, bewegt sich genau der (`scrollImBehaelter`). Gibt es keinen, wie
 * auf den Vollseiten `/kunden/$bcNumber` und `/projekte/$key`, in denen das
 * Dokument scrollt, greift `scrollIntoView`. Bewacht ist dieser Rückfall:
 * Versteckt ein Vorfahre den Überlauf, unterbleibt er, denn genau den Knoten
 * liesse `scrollIntoView` verschoben stehen (ADR-0097, Befund Fabian
 * 17.09.2026).
 *
 * `block` folgt der Lage: `mitte` an die Oberkante (`start`), `naechste`
 * gerade so ins Bild (`nearest`). `center` wäre die wörtliche Entsprechung von
 * `mitte`, ist auf der Vollseite aber die falsche: Die Ziele dort sind
 * Sprungmarken mit `scroll-mt-4` (`section-card.tsx`, die Abschnitte in
 * `customer-detail.tsx`), und bei `center` stünde eine lange Karte mit ihrer
 * Mitte im Bild und ihre Überschrift oberhalb des Rands. Im Behälter bleibt `mitte`
 * mittig, weil dort die Karte kurz und der Ausschnitt klein ist. `nearest`
 * beachtet `scroll-mt-*` ebenfalls.
 *
 * @returns `true`, wenn gesprungen wurde oder nichts zu tun war, `false`, wenn
 *   der Sprung unterblieb (Überlauf versteckt oder kein `scrollIntoView` in
 *   dieser Umgebung).
 */
export function scrollInsBild(
  ziel: HTMLElement,
  lage: ScrollLage,
  verhalten: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth',
): boolean {
  if (scrollImBehaelter(ziel, lage, verhalten)) {
    return true;
  }
  if (verstecktEinVorfahreDenUeberlauf(ziel)) {
    return false;
  }
  if (typeof ziel.scrollIntoView !== 'function') {
    return false;
  }
  ziel.scrollIntoView({ behavior: verhalten, block: lage === 'naechste' ? 'nearest' : 'start' });
  return true;
}

/**
 * Versteckt ein Vorfahre des Ziels den senkrechten Überlauf?
 *
 * Die Frage entscheidet, ob der Rückfall auf `scrollIntoView` erlaubt ist:
 * Einen solchen Knoten verschöbe er mit, und niemand zöge ihn zurück (Befund
 * Fabian, 17.09.2026, Fenster-Modell ADR-0097). Geprüft wird die Klasse und
 * der berechnete Stil, wie es der Helfer für die scrollenden Behälter tut:
 * jsdom rechnet keine Stylesheets, im Browser zählt der berechnete Stil.
 *
 * Die Schleife läuft bis einschliesslich `html`. Der Scroll-Lock eines Dialogs
 * setzt `overflow: hidden` auf `body` (Base UI), und eine Vollbild-Sperre tut
 * dasselbe an `html`: Beide blieben unbemerkt, solange die Prüfung vor `body`
 * endete.
 */
export function verstecktEinVorfahreDenUeberlauf(ziel: HTMLElement): boolean {
  let knoten: HTMLElement | null = ziel.parentElement;
  while (knoten !== null) {
    const klassen = knoten.classList;
    if (UEBERLAUF_VERSTECKT.some((klasse) => klassen.contains(klasse))) {
      return true;
    }
    const overflowY = window.getComputedStyle(knoten).overflowY;
    if (overflowY === 'hidden' || overflowY === 'clip') {
      return true;
    }
    knoten = knoten.parentElement;
  }
  return false;
}

/**
 * Der `scroll-margin-top` des Ziels in Pixeln, `0` wo keiner steht oder die
 * Umgebung ihn nicht rechnet (eine leere Zeichenkette statt einer Länge).
 */
function scrollRandOben(ziel: HTMLElement): number {
  const wert = Number.parseFloat(window.getComputedStyle(ziel).scrollMarginTop);
  return Number.isNaN(wert) ? 0 : wert;
}

/** Der nächste Vorfahre, der wirklich scrollt (`null`, wenn es keinen gibt). */
function naechsterScrollBehaelter(ziel: HTMLElement): HTMLElement | null {
  let knoten = ziel.parentElement;
  while (knoten !== null) {
    if (scrollt(knoten)) {
      return knoten;
    }
    knoten = knoten.parentElement;
  }
  return null;
}

/**
 * Scrollt dieser Knoten senkrecht? Klasse oder gerechneter Stil genügt.
 *
 * Eine nur waagerechte Leiste zählt nicht, auch wenn ihr berechneter
 * `overflowY` im Browser `auto` meldet (siehe `WAAGERECHT_KLASSEN`).
 */
function scrollt(knoten: HTMLElement): boolean {
  if (SCROLL_KLASSEN.some((klasse) => knoten.classList.contains(klasse))) {
    return true;
  }
  if (WAAGERECHT_KLASSEN.some((klasse) => knoten.classList.contains(klasse))) {
    return false;
  }
  const senkrecht = window.getComputedStyle(knoten).overflowY;
  return senkrecht === 'auto' || senkrecht === 'scroll';
}

/**
 * Setzt den Scroll-Stand: nie negativ, nie über das Ende hinaus.
 *
 * Der Browser klemmt selbst, aber erst beim Setzen. Mit `behavior: 'smooth'`
 * liefe die Animation sonst gegen den Anschlag, und der gerechnete Wert
 * stimmte nicht mit dem, den die Fläche danach trägt (Prüfbefund 18.09.2026).
 *
 * `scrollTo` fehlt in jsdom, `scrollTop` gibt es dort: deshalb der Rückfall.
 * Dort ist `scrollHeight` immer 0, ein Ende gibt es also nicht zu klemmen.
 */
function scrolleAuf(behaelter: HTMLElement, top: number, verhalten: ScrollBehavior): void {
  const ziel = geklemmt(behaelter, top);
  if (typeof behaelter.scrollTo === 'function') {
    behaelter.scrollTo({ top: ziel, behavior: verhalten });
    return;
  }
  behaelter.scrollTop = ziel;
}

/** Der Wunsch-Stand, begrenzt auf das, was der Behälter hergibt. */
function geklemmt(behaelter: HTMLElement, top: number): number {
  const oben = Math.max(0, top);
  if (behaelter.scrollHeight <= 0) {
    return oben;
  }
  return Math.min(Math.max(0, behaelter.scrollHeight - behaelter.clientHeight), oben);
}
